import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button, Card, TextField, Typography, CircularProgress, Alert, Container, Box } from "@mui/material";
import ManagerApi from "../../api/ManagerApi";
import UnauthNav from "../../components/UnauthNav/UnauthNav";

import "./ManagerPasswordResetView.scss";

const ManagerPasswordResetView = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const expiresAt = searchParams.get('expiresAt');
  const navigate = useNavigate();
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Invalid password reset link. Please request a new password reset.");
      return;
    }

    if (expiresAt) {
      const expirationTime = Number(expiresAt);
      if (!Number.isNaN(expirationTime) && Date.now() > expirationTime) {
        setIsExpired(true);
      }
    }
  }, [token, expiresAt]);

  const validatePassword = (password: string): string => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(password)) {
      return "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character";
    }
    return "";
  };

  const handlePasswordChange = (password: string) => {
    setNewPassword(password);
    const validation = validatePassword(password);
    setPasswordError(validation);
  };

  const handleSubmit = async () => {
    if (!token) {
      setError("Invalid password reset link");
      return;
    }

    if (isExpired) {
      setError("This password reset link has expired. Please request a new one.");
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const validation = validatePassword(newPassword);
    if (validation) {
      setError(validation);
      return;
    }

    setLoading(true);
    setError("");

    try {
      await ManagerApi.confirmPasswordReset(token, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate("/manager-login");
      }, 3000);
    } catch (err: any) {
      setError(err.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !loading && newPassword && confirmPassword) {
      handleSubmit();
    }
  };

  if (success) {
    return (
      <>
        <UnauthNav />
        <Container maxWidth="sm" className="password-reset-container">
          <Card className="password-reset-card">
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="h6">Manager Password Reset Successful!</Typography>
              <Typography variant="body2">
                Your manager password has been reset successfully. You will be redirected to the manager login page in a few seconds.
              </Typography>
            </Alert>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => navigate("/manager-login")}
              fullWidth
            >
              Go to Manager Login
            </Button>
          </Card>
        </Container>
      </>
    );
  }

  if (isExpired) {
    return (
      <>
        <UnauthNav />
        <Container maxWidth="sm" className="password-reset-container">
          <Card className="password-reset-card">
            <Typography variant="h4" component="h1" className="password-reset-title">
              Password Reset Link Expired
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="h6">Link Has Expired</Typography>
              <Typography variant="body2">
                This password reset link has expired. Password reset links are only valid for 24 hours after the request is created for security reasons.
              </Typography>
            </Alert>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Please request a new password reset to continue. You can do this from the manager login page.
            </Typography>

            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => navigate("/manager-login")}
              fullWidth
              size="large"
            >
              Go to Manager Login
            </Button>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <UnauthNav />
      <Container maxWidth="sm" className="password-reset-container">
        <Card className="password-reset-card">
          <Typography variant="h4" component="h1" className="password-reset-title">
            Reset Manager Password
          </Typography>
          
          <Typography variant="body2" color="textSecondary" className="password-reset-subtitle">
            Please enter your new manager password below. Make sure it's secure and easy for you to remember.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box className="password-reset-form">
            <TextField
              label="New Manager Password"
              type="password"
              value={newPassword}
              onChange={(e) => handlePasswordChange(e.target.value)}
              onKeyDown={handleKeyPress}
              variant="outlined"
              fullWidth
              disabled={loading}
              error={!!passwordError}
              helperText={passwordError || "At least 8 characters with uppercase, lowercase, number, and special character"}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={handleKeyPress}
              variant="outlined"
              fullWidth
              disabled={loading}
              error={newPassword !== confirmPassword && confirmPassword.length > 0}
              helperText={newPassword !== confirmPassword && confirmPassword.length > 0 ? "Passwords do not match" : ""}
              sx={{ mb: 3 }}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading || !newPassword || !confirmPassword || !!passwordError || newPassword !== confirmPassword}
              fullWidth
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "Resetting Password..." : "Reset Manager Password"}
            </Button>
          </Box>
        </Card>
      </Container>
    </>
  );
};

export default ManagerPasswordResetView;